import React, { useState, useEffect, useRef, useMemo } from 'react';
import { LoaderCircle, X, FileText, Search, MoveDown, MoveUp,ZoomIn, ZoomOut, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import { getSourceDownloadUrl } from '../../service/apiService';
import { getDisplayFileName } from '../../hooks/useFileUpload';
import { useSession } from '../../context/SessionContext';
import * as XLSX from 'xlsx';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

interface FilePreviewProps {
  isOpen: boolean;
  onClose: () => void;
  fileInfo: {
    fileKey: string;
    fileKind: string;
    indexName?: string;
  } | null;
}

const FilePreview: React.FC<FilePreviewProps> = ({ isOpen, onClose, fileInfo }) => {
  const [loading, setLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [fileType, setFileType] = useState<string>('unknown');
  // The user's original filename for the modal header. Files are stored under a
  // minted key (e.g. "sheet-1a2b3c4d.xlsx"); the backend resolves the real name
  // (report.xlsx, including unicode/emoji names) into display_file_name. Until that
  // loads, fall back to the client-side resolver (shared originalNames map / suffix strip).
  const [displayName, setDisplayName] = useState<string>('');
  const { sessionId } = useSession();
  const modalRef = useRef<HTMLDivElement>(null);
  
  // For text file viewing
  const [content, setContent] = useState<string | null>(null);
  const [excelData, setExcelData] = useState<any[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  // For search functionality
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<number[]>([]);
  const [currentSearchIndex, setCurrentSearchIndex] = useState(-1);
  const contentRef = useRef<HTMLDivElement>(null);

  // PDF specific states
  const [numPages, setNumPages] = useState<number>(0);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [scale, setScale] = useState<number>(1.0);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  // NEW: Track if file is deleted
  const [isFileDeleted, setIsFileDeleted] = useState(false);

  // PDF.js options - defined to avoid re-renders
  const pdfOptions = useMemo(() => ({
    cMapUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/cmaps/`,
    cMapPacked: true,
    standardFontDataUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/standard_fonts/`,
    wasmUrl: `https://unpkg.com/pdfjs-dist@${pdfjs.version}/wasm/`
  }), []);

  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    window.addEventListener('keydown', handleEsc);
    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose]);

  // Handle outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(event.target as Node) && isOpen) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  // Keyboard navigation for PDF
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (fileType !== 'pdf' || numPages === 0) return;
      
      switch (event.key) {
        case 'ArrowLeft':
        case 'ArrowUp':
          event.preventDefault();
          goToPrevPage();
          break;
        case 'ArrowRight':
        case 'ArrowDown':
          event.preventDefault();
          goToNextPage();
          break;
        case 'Home':
          event.preventDefault();
          setPageNumber(1);
          break;
        case 'End':
          event.preventDefault();
          setPageNumber(numPages);
          break;
        case '+':
        case '=':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            zoomIn();
          }
          break;
        case '-':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            zoomOut();
          }
          break;
        case '0':
          if (event.ctrlKey || event.metaKey) {
            event.preventDefault();
            resetZoom();
          }
          break;
      }
    };

    if (fileType === 'pdf') {
      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }
  }, [fileType, numPages]);

  useEffect(() => {
    const loadPreview = async () => {
      if (!fileInfo) return;
      
      try {
        setLoading(true);
        setError(null);
        setIsFileDeleted(false); // NEW: Reset deleted state
        
        // ✅ NEW CODE - Get full response from API
        const response = await getSourceDownloadUrl(
          fileInfo.fileKind.toLowerCase(),
          fileInfo.fileKey,
          sessionId,
          fileInfo.indexName,
          3600 // 1 hour expiry
        );

       // ✅ Extract presigned_url, file_name and the resolved original name
       const { presigned_url, file_name, display_file_name } = response;

       // Prefer the backend-resolved original name for the header; fall back to
       // the client-side resolver so it's correct even before/without that field.
       setDisplayName(display_file_name || getDisplayFileName(fileInfo.fileKey));

      // ✅ Use file_name from API (has correct extension)
      const actualFileName = file_name || fileInfo.fileKey;
      const fileExtension = actualFileName.split('.').pop()?.toLowerCase() || '';
      setFileType(getFileType(fileExtension));  // ✅ Now correctly detects "pdf"!

       setPreviewUrl(presigned_url);
        
        // Fetch content for text files
        if (fileExtension !== 'pdf' && fileExtension !== 'xlsx' && fileExtension !== 'xls') {
          try {
            const response = await fetch(presigned_url);
            if (!response.ok) throw new Error(`Failed to fetch file: ${response.status}`);
            const text = await response.text();
            setContent(text);
          } catch (err) {
            console.error("Error fetching file content:", err);
            setError("Failed to load text content");
          }
        }
        
        // Fetch and parse Excel files
        if (fileExtension === 'xlsx' || fileExtension === 'xls') {
          try {
            const response = await fetch(presigned_url);
            if (!response.ok) throw new Error(`Failed to fetch file: ${response.status}`);

            const arrayBuffer = await response.arrayBuffer();
            const data = new Uint8Array(arrayBuffer);

            const workbook = XLSX.read(data, { type: 'array' });
            const firstSheetName = workbook.SheetNames[0];
            const worksheet = workbook.Sheets[firstSheetName];

            const jsonData = XLSX.utils.sheet_to_json(worksheet);
            setExcelData(jsonData);
          } catch (err) {
            console.error("Error parsing Excel file:", err);
            setError("Cannot parse Excel file");
          }
        }
      } catch (error) {
        console.error("Error generating preview URL:", error);
        
        // NEW: Check if this is a "file not found" error from the API
        const errorMessage = (error as any)?.message || '';
        const errorResponse = (error as any)?.response;
        
        // Check for 404 or file not found indicators
        if (errorResponse?.status === 404 || 
            errorMessage.toLowerCase().includes('not found') ||
            errorMessage.toLowerCase().includes('does not exist')) {
          setIsFileDeleted(true);
          setError("This file has been removed from the BrainBank and is no longer available.");
        } else {
          toast.error(`Failed to preview file: ${fileInfo.fileKey}`);
          setError("Failed to load preview");
        }
      } finally {
        setLoading(false);
      }
    };
    
    if (isOpen && fileInfo) {
      loadPreview();
    } else {
      // Clear preview when modal is closed
      setPreviewUrl(null);
      setDisplayName('');
      setContent(null);
      setExcelData(null);
      setError(null);
      setSearchTerm("");
      setSearchResults([]);
      setCurrentSearchIndex(-1);
      setNumPages(0);
      setPageNumber(1);
      setScale(1.0);
      setPdfError(null);
      setPdfLoading(false);
      setIsFileDeleted(false); // NEW: Reset deleted state
    }
  }, [isOpen, fileInfo, sessionId]);
  
  // Search functionality
  useEffect(() => {
    if (!content || !searchTerm.trim()) {
      setSearchResults([]);
      setCurrentSearchIndex(-1);
      return;
    }
    
    const results: number[] = [];
    const text = content.toLowerCase();
    const searchTermLower = searchTerm.toLowerCase();
    let index = 0;
    
    while (index < text.length) {
      index = text.indexOf(searchTermLower, index);
      if (index === -1) break;
      results.push(index);
      index += searchTermLower.length;
    }
    
    setSearchResults(results);
    setCurrentSearchIndex(results.length > 0 ? 0 : -1);
  }, [content, searchTerm]);
  
  // Handle search navigation
  const handleSearchNext = () => {
    if (searchResults.length === 0) return;
    setCurrentSearchIndex((prev) => (prev + 1) % searchResults.length);
  };
  
  const handleSearchPrevious = () => {
    if (searchResults.length === 0) return;
    setCurrentSearchIndex((prev) => (prev - 1 + searchResults.length) % searchResults.length);
  };
  
  // Scroll to the current search result
  useEffect(() => {
    if (currentSearchIndex >= 0 && searchResults.length > 0 && contentRef.current) {
      const currentResult = searchResults[currentSearchIndex];
      const textUptoCurrent = content?.substring(0, currentResult) || '';
      
      const lines = textUptoCurrent.split('\n');
      const lineIndex = lines.length - 1;
      
      const lineElement = contentRef.current.querySelector(`[data-line-number="${lineIndex + 1}"]`);
      if (lineElement) {
        lineElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [currentSearchIndex, searchResults, content]);

  // PDF Document Load Success
  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPageNumber(1);
    setPdfError(null);
    setPdfLoading(false);
  };

  // PDF Document Load Start
  const onDocumentLoadStart = () => {
    setPdfLoading(true);
    setPdfError(null);
  };

  // PDF Document Load Error - NEW: Enhanced error handling
  const onDocumentLoadError = (error: Error) => {
    console.error('PDF load error:', error);
    
    // Check if error indicates file is missing/deleted
    const errorMsg = error.message.toLowerCase();
    if (errorMsg.includes('missing pdf') || 
        errorMsg.includes('404') || 
        errorMsg.includes('not found')) {
      setIsFileDeleted(true);
      setPdfError("This PDF has been removed from the BrainBank.");
    } else {
      setPdfError(`Failed to load PDF: ${error.message}`);
    }
    setPdfLoading(false);
  };

  // PDF Page Load Success
  const onPageLoadSuccess = () => {
    // Page loaded successfully
  };

  // PDF Page Load Error
  const onPageLoadError = (error: Error) => {
    console.error('PDF page load error:', error);
  };

  // PDF Page Navigation
  const goToPrevPage = () => setPageNumber(prev => Math.max(prev - 1, 1));
  const goToNextPage = () => setPageNumber(prev => Math.min(prev + 1, numPages));
  const goToPage = (page: number) => setPageNumber(Math.max(1, Math.min(page, numPages)));

  // PDF Zoom Controls
  const zoomIn = () => setScale(prev => Math.min(prev + 0.2, 3.0));
  const zoomOut = () => setScale(prev => Math.max(prev - 0.2, 0.5));
  const resetZoom = () => setScale(1.0);
  
  // Helper function to determine file type
  const getFileType = (extension: string): string => {
    const imageTypes = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp'];
    const pdfTypes = ['pdf'];
    const textTypes = ['txt', 'md', 'rtf', 'json', 'csv', 'html', 'xml', 'js', 'ts', 'jsx', 'tsx', 'css', 'scss'];
    const excelTypes = ['xlsx', 'xls'];
    const wordTypes = ['docx', 'doc'];
    
    if (imageTypes.includes(extension)) return 'image';
    if (pdfTypes.includes(extension)) return 'pdf';
    if (textTypes.includes(extension)) return 'text';
    if (excelTypes.includes(extension)) return 'excel';
    if (wordTypes.includes(extension)) return 'word';
    
    return 'unknown';
  };
  
  // Create Excel viewer URL
  const createExcelViewerUrl = () => {
    if (!previewUrl) return '';
    const encodedFileUrl = encodeURIComponent(previewUrl);
    return `https://view.officeapps.live.com/op/view.aspx?src=${encodedFileUrl}`;
  };

  // Generate Google Docs viewer URL for Word files only
  const getGoogleDocsViewerUrl = () => {
    if (!previewUrl) return '';
    return `https://docs.google.com/viewer?embedded=true&url=${encodeURIComponent(previewUrl)}`;
  };
  
  // Render Excel table as fallback
  const renderExcelTable = () => {
    if (!excelData || excelData.length === 0) return <p>No data found in the Excel file.</p>;

    const headers = Object.keys(excelData[0]);

    return (
      <div className="overflow-auto max-h-[calc(80vh-10rem)]">
        <table className="min-w-full bg-white border border-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {headers.map((header, index) => (
                <th key={index} className="px-4 py-2 text-left text-sm font-semibold border">
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {excelData.map((row, rowIndex) => (
              <tr key={rowIndex}>
                {headers.map((header, colIndex) => (
                  <td key={colIndex} className="px-4 py-2 border">
                    {row[header] !== undefined ? String(row[header]) : ''}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };
  
  // Render text content with search highlighting
  const renderTextContent = () => {
    if (!content) return null;
    
    // Split content into lines
    const lines = content.split('\n');
    
    return (
      <div ref={contentRef} className="text-sm font-mono p-4">
        <div className="overflow-x-auto">
          {lines.map((line, lineIndex) => {
            if (searchTerm.trim() === '') {
              return (
                <div key={`line-${lineIndex}`} className="leading-6" data-line-number={lineIndex + 1}>
                  {line || ' '}
                </div>
              );
            }
            
            const lineSearchTerm = searchTerm.toLowerCase();
            const lineLower = line.toLowerCase();
            const matches: { index: number, length: number }[] = [];
            let searchIndex = 0;
            
            while (searchIndex < line.length) {
              const foundIndex = lineLower.indexOf(lineSearchTerm, searchIndex);
              if (foundIndex === -1) break;
              matches.push({ index: foundIndex, length: searchTerm.length });
              searchIndex = foundIndex + lineSearchTerm.length;
            }
            
            if (matches.length === 0) {
              return (
                <div key={`line-${lineIndex}`} className="leading-6" data-line-number={lineIndex + 1}>
                  {line || ' '}
                </div>
              );
            }
            
            const parts: JSX.Element[] = [];
            let lastIndex = 0;
            
            const lineStartIndex = content.split('\n').slice(0, lineIndex).join('\n').length + (lineIndex > 0 ? 1 : 0);
            
            matches.forEach((match, matchIndex) => {
              const absoluteMatchIndex = lineStartIndex + match.index;
              
              if (match.index > lastIndex) {
                parts.push(
                  <span key={`before-${lineIndex}-${matchIndex}`}>
                    {line.substring(lastIndex, match.index)}
                  </span>
                );
              }
              
              const isCurrentMatch = searchResults[currentSearchIndex] === absoluteMatchIndex;
              parts.push(
                <span 
                  key={`highlight-${lineIndex}-${matchIndex}`} 
                  className={isCurrentMatch ? 
                    "bg-yellow-400 text-black" : 
                    "bg-yellow-200 text-black"}
                  id={isCurrentMatch ? "current-search-result" : undefined}
                >
                  {line.substring(match.index, match.index + match.length)}
                </span>
              );
              
              lastIndex = match.index + match.length;
            });
            
            if (lastIndex < line.length) {
              parts.push(
                <span key={`after-${lineIndex}`}>
                  {line.substring(lastIndex)}
                </span>
              );
            }
            
            return (
              <div key={`line-${lineIndex}`} className="leading-6" data-line-number={lineIndex + 1}>
                {parts.length > 0 ? parts : ' '}
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Render PDF Viewer
  const renderPdfViewer = () => {
    return (
      <div className="h-full flex flex-col bg-gray-100">
        {/* PDF Controls */}
        <div className="flex items-center justify-between p-3 bg-white border-b border-gray-200 flex-wrap gap-2">
          {/* Page Navigation */}
          <div className="flex items-center space-x-2">
            <button
              onClick={goToPrevPage}
              disabled={pageNumber <= 1}
              className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Previous page (← or ↑)"
            >
              <ChevronLeft size={20} />
            </button>
            
            <div className="flex items-center space-x-2">
              <input
                type="number"
                min={1}
                max={numPages}
                value={pageNumber}
                onChange={(e) => goToPage(parseInt(e.target.value) || 1)}
                className="w-16 bg-white text-gray-900 text-center border border-gray-300 rounded px-2 py-1 text-sm"
              />
              <span className="text-sm text-gray-600">of {numPages}</span>
            </div>
            
            <button
              onClick={goToNextPage}
              disabled={pageNumber >= numPages}
              className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Next page (→ or ↓)"
            >
              <ChevronRight size={20} />
            </button>
          </div>

          {/* Zoom Controls */}
          <div className="flex items-center space-x-2">
            <button
              onClick={zoomOut}
              disabled={scale <= 0.5}
              className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Zoom out (Ctrl + -)"
            >
              <ZoomOut size={16} />
            </button>
            
            <span className="text-sm text-gray-600 min-w-[4rem] text-center">
              {Math.round(scale * 100)}%
            </span>
            
            <button
              onClick={zoomIn}
              disabled={scale >= 3.0}
              className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              title="Zoom in (Ctrl + +)"
            >
              <ZoomIn size={16} />
            </button>
            
            <button
              onClick={resetZoom}
              className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-100"
              title="Reset zoom (Ctrl + 0)"
            >
              Reset
            </button>
          </div>
        </div>

        {/* PDF Document */}
        <div className="flex-1 overflow-auto p-4 bg-gray-100">
          <div className="flex justify-center">
            <div className="bg-white shadow-lg">
              <Document
                file={previewUrl}
                onLoadStart={onDocumentLoadStart}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                options={pdfOptions}
                loading={
                  <div className="flex flex-col justify-center items-center p-8 min-h-[400px]">
                    <LoaderCircle className="animate-spin h-8 w-8 text-violet-500 mb-2" />
                    <span className="text-sm text-gray-600">Loading PDF...</span>
                    <span className="text-xs text-gray-500 mt-1">This may take a moment for large files</span>
                  </div>
                }
                error={
  <div className="flex flex-col items-center justify-center p-8 min-h-[400px]">
    <div className="text-center max-w-md">
      <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
        <FileText className="w-8 h-8 text-blue-500" />
      </div>
      <p className="text-gray-600 leading-relaxed">
        This file is not available or has been removed from the BrainBank.
      </p>
    </div>
  </div>
}
              >
                <Page
                  pageNumber={pageNumber}
                  scale={scale}
                  onLoadSuccess={onPageLoadSuccess}
                  onLoadError={onPageLoadError}
                  loading={
                    <div className="flex justify-center items-center p-4 min-h-[400px]">
                      <LoaderCircle className="animate-spin h-6 w-6 text-violet-500" />
                      <span className="ml-2 text-sm text-gray-600">Loading page...</span>
                    </div>
                  }
                  error={
                    <div className="text-red-500 p-4 text-center">
                      <p>Failed to load page {pageNumber}</p>
                    </div>
                  }
                />
              </Document>
            </div>
          </div>
        </div>

        {/* PDF Keyboard shortcuts info */}
        <div className="px-3 py-2 bg-gray-50 border-t border-gray-200">
          <div className="text-xs text-gray-500 text-center">
            Use arrow keys to navigate • Ctrl/Cmd + +/- to zoom • Home/End for first/last page
          </div>
        </div>
      </div>
    );
  };
  
  if (!isOpen || !fileInfo) return null;
  
  return (
    // Fixed position overlay with centered content
    <div className="fixed inset-0 scrim flex items-center justify-center z-[9999]" style={{ backdropFilter: 'blur(3px)' }}>
      {/* Modal container with max-width and height limits */}
      <div 
        ref={modalRef}
        className="bg-white rounded-xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col mx-auto"
        style={{ height: '85vh' }} 
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with file name and close button */}
        <div className="flex justify-between items-center p-4 border-b border-gray-200">
          <h2 className="text-xl font-semibold flex items-center truncate">
            <FileText className="h-5 w-5 mr-2 text-violet-600 flex-shrink-0" />
            <span className="truncate">{displayName || getDisplayFileName(fileInfo.fileKey)}</span>
          </h2>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onClose();
            }}
            className="text-gray-500 hover:text-gray-700 p-2 rounded-full hover:bg-gray-100 z-[9999]"
          >
            <X size={20} />
          </button>
        </div>
        
        {/* Content area */}
        <div className="flex-1 overflow-hidden">
          {loading ? (
            <div className="flex justify-center items-center h-full">
              <LoaderCircle className="animate-spin h-8 w-8 text-violet-500" />
            </div>
          ) : error ? (
  /* Simplified error display for all file types */
  <div className="flex flex-col items-center justify-center h-full p-8">
    <div className="text-center max-w-md">
      <div className="w-16 h-16 mx-auto mb-4 bg-blue-100 rounded-full flex items-center justify-center">
        <FileText className="w-8 h-8 text-blue-500" />
      </div>
      <p className="text-gray-600 leading-relaxed">
        This file is not available or has been removed from the BrainBank.
      </p>
    </div>
  </div>
) : (
            <div className="h-full">
              {/* Image viewer */}
              {fileType === 'image' && previewUrl && (
                <div className="flex items-center justify-center h-full p-4">
                  <img 
                    src={previewUrl} 
                    alt={fileInfo.fileKey} 
                    className="max-w-full max-h-[calc(85vh-8rem)] object-contain"
                  />
                </div>
              )}
              
              {/* PDF viewer with proper centering */}
              {fileType === 'pdf' && previewUrl && renderPdfViewer()}

              {/* Excel viewer */}
              {fileType === 'excel' && previewUrl && (
                <div className="h-full w-full mx-auto" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 0 }}>
                  <iframe
                    src={createExcelViewerUrl()}
                    style={{ 
                      width: '100%', 
                      height: '100%', 
                      border: 'none',
                      display: 'block',
                      margin: '0 auto'
                    }}
                    title="Excel Viewer"
                    frameBorder="0"
                    allowFullScreen
                  ></iframe>
                </div>
              )}
              
              {/* Word document viewer - NOW USING GOOGLE DOCS */}
              {fileType === 'word' && previewUrl && (
                <div className="h-full w-full mx-auto" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 0 }}>
                  <iframe
                    src={getGoogleDocsViewerUrl()}
                    style={{ 
                      width: '100%', 
                      height: '100%', 
                      border: 'none',
                      display: 'block',
                      margin: '0 auto'
                    }}
                    title="Word Viewer"
                    frameBorder="0"
                    allowFullScreen
                  ></iframe>
                </div>
              )}
              
              {/* Text viewer with search */}
              {fileType === 'text' && (
                <div className="h-full flex flex-col">
                  <div className="flex items-center p-2 bg-gray-100 border-b border-gray-200">
                    <div className="relative flex-grow">
                      <span className="absolute inset-y-0 left-0 flex items-center pl-2">
                        <Search size={16} className="text-gray-500" />
                      </span>
                      <input
                        type="text"
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Search in text..."
                        className="w-full py-1 pl-8 pr-3 bg-white text-gray-900 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-violet-500"
                      />
                    </div>
                    <div className="flex items-center ml-2">
                      <span className="text-sm text-gray-600 mr-2">
                        {searchResults.length > 0 ? 
                          `${currentSearchIndex + 1} of ${searchResults.length}` : 
                          "No results"}
                      </span>
                      <button 
                        onClick={handleSearchPrevious}
                        disabled={searchResults.length === 0}
                        className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Previous match"
                      >
                        <MoveUp size={16} className="text-gray-700" />
                      </button>
                      <button 
                        onClick={handleSearchNext}
                        disabled={searchResults.length === 0}
                        className="p-1 rounded hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Next match"
                      >
                        <MoveDown size={16} className="text-gray-700" />
                      </button>
                    </div>
                  </div>
                  
                  <div className="flex-1 overflow-auto custom-scrollbar">
                    {renderTextContent()}
                  </div>
                </div>
              )}
              
              {/* Unknown file type message */}
              {fileType === 'unknown' && (
                <div className="flex flex-col items-center justify-center h-full py-10">
                  <p className="text-gray-500 mb-4">Preview not available for this file type.</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FilePreview;